import { useEffect, useMemo, useRef, useState } from "react";
import { PhoneCall, Clock, Calendar, User, Pill, Radio, AlertTriangle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResultBadge, EnumBadge } from "@/components/badges";
import { TranscriptView } from "@/components/transcript-view";
import { StructuredOutputView } from "@/components/structured-output-view";
import { formatDuration, formatPersonName, formatDate } from "@/lib/formatters";
import { DATE_FORMAT_FULL } from "@/lib/constants";
import config from "@/config";
import type { CallRow, CaseRecord, TranscriptEntry, TranscriptMessage } from "@/types";

// ─── Shared sub-component ────────────────────────────────────────────

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof PhoneCall;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium">{value || "-"}</span>
    </div>
  );
}

// ─── Attempt Selector ────────────────────────────────────────────────

interface AttemptSelectorProps {
  attempts: { success?: string }[];
  value: number;
  onChange: (idx: number) => void;
}

function AttemptSelector({ attempts, value, onChange }: AttemptSelectorProps) {
  if (attempts.length <= 1) return null;

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">
        Attempt
      </label>
      <Select
        value={String(value)}
        onValueChange={(v) => onChange(Number(v))}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {attempts.map((so, i) => (
            <SelectItem key={i} value={String(i)}>
              Attempt {i + 1} — {so.success === "true" ? "Success" : "Failed"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─── Call Info Section ───────────────────────────────────────────────

interface CallInfoSectionProps {
  phoneNumber: string;
  duration?: number;
  timestamp: unknown;
  scheduledFor?: string;
  voiceProvider?: string | null;
  nextRetryProvider?: string | null;
}

function providerLabel(v: string | null | undefined): string {
  if (!v) return "";
  return v === "asepha_voice" ? "asepha-voice" : v;
}

function CallInfoSection({
  phoneNumber,
  duration,
  timestamp,
  scheduledFor,
  voiceProvider,
  nextRetryProvider,
}: CallInfoSectionProps) {
  const providerText = (() => {
    const cur = providerLabel(voiceProvider);
    const next = providerLabel(nextRetryProvider);
    if (cur && next && next !== cur) return `${cur} → ${next} on next retry`;
    if (cur) return cur;
    if (next) return `→ ${next} on next retry`;
    return "";
  })();
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">Call Info</h4>
      <InfoRow icon={PhoneCall} label="Phone" value={phoneNumber} />
      <InfoRow icon={Clock} label="Duration" value={formatDuration(duration)} />
      <InfoRow icon={Calendar} label="Time" value={formatDate(timestamp, DATE_FORMAT_FULL)} />
      {providerText ? (
        <InfoRow icon={Radio} label="Provider" value={providerText} />
      ) : null}
      {scheduledFor ? (
        <InfoRow
          icon={Calendar}
          label="Scheduled to ring"
          value={formatDate(scheduledFor, DATE_FORMAT_FULL)}
        />
      ) : null}
    </div>
  );
}

// ─── Patient Info Section ───────────────────────────────────────────

interface PatientInfoSectionProps {
  name: string;
  dob: string;
  memberId: string;
  payer: string;
  medication: string;
}

function PatientInfoSection({ name, dob, memberId, payer, medication }: PatientInfoSectionProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">Patient Info</h4>
      <InfoRow icon={User} label="Name" value={name} />
      <InfoRow icon={User} label="DOB" value={dob} />
      <InfoRow icon={User} label="Member ID" value={memberId} />
      <InfoRow icon={User} label="Payer" value={payer} />
      <InfoRow icon={Pill} label="Medication" value={medication} />
    </div>
  );
}

// ─── Pre-call Prefill Notice ────────────────────────────────────────
// Case-level: surfaces when Stedi/pricing pre-call synthesis failed, so a
// "wonky" case (agent dialed without prefills — e.g. missing practice_tax_id)
// is obvious at a glance. Renders nothing for ok/empty/absent (non-breaking).

function PrecallStatusNotice({
  status,
  error,
  updatedAt,
}: {
  status?: string;
  error?: string | null;
  updatedAt?: string;
}) {
  if (status !== "failed" && status !== "empty") return null;
  const isFailed = status === "failed";
  const wrap = isFailed
    ? "border-destructive/40 bg-destructive/10 text-destructive"
    : "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200";
  return (
    <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${wrap}`}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-1">
        <div className="font-medium">
          {isFailed ? "Pre-call prefill failed" : "No pre-call prefills"}
        </div>
        <p className={isFailed ? "text-destructive/90" : "text-amber-900/90 dark:text-amber-200/90"}>
          This case was dialed without Stedi / pricing prefills, so structured-output
          fields (e.g. insurance active status, max-allowed amounts) may be blank or
          unreliable.
        </p>
        {error ? (
          <p className={`font-mono text-xs break-all ${isFailed ? "text-destructive/80" : "text-amber-900/80 dark:text-amber-200/80"}`}>
            {error}
          </p>
        ) : null}
        {updatedAt ? (
          <p className={`text-xs ${isFailed ? "text-destructive/70" : "text-amber-900/70 dark:text-amber-200/70"}`}>
            Last checked {formatDate(updatedAt, DATE_FORMAT_FULL)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

interface CallDetailSheetProps {
  callRow: CallRow | null;
  onClose: () => void;
}

export function CallDetailSheet({ callRow, onClose }: CallDetailSheetProps) {
  const [attemptIdx, setAttemptIdx] = useState(0);
  const [fetchedTxByCallId, setFetchedTxByCallId] = useState<
    Record<string, TranscriptEntry>
  >({});
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);
  const fetchSeqRef = useRef(0);
  const fetchedTxRef = useRef<Record<string, TranscriptEntry>>({});

  const caseData = callRow?.case as CaseRecord | undefined;
  const soHistory = caseData?.structured_outputs_history ?? [];
  const txHistory = caseData?.call_transcript_history ?? [];

  useEffect(() => {
    if (callRow) {
      setAttemptIdx(callRow.attemptIndex);
      setFetchedTxByCallId({});
      fetchedTxRef.current = {};
      setTxError(null);
      setTxLoading(false);
      fetchSeqRef.current += 1;
    }
  }, [callRow?.rowKey, callRow?.attemptIndex, caseData?.id]);

  const currentSo = soHistory[attemptIdx];

  const embeddedTx = useMemo(() => {
    const list = txHistory;
    const cid = currentSo?.call_id;
    if (cid) {
      const match = list.find((t) => t.call_id === cid);
      if (match?.transcript) return match;
    }
    const byIndex = list[attemptIdx];
    if (byIndex?.transcript) return byIndex;
    return undefined;
  }, [txHistory, attemptIdx, currentSo?.call_id]);

  const callIdForTx = (currentSo?.call_id && String(currentSo.call_id).trim()) || "";

  useEffect(() => {
    if (!caseData?.id || !callIdForTx || embeddedTx) {
      setTxLoading(false);
      setTxError(null);
      return;
    }
    if (fetchedTxRef.current[callIdForTx]) {
      setTxLoading(false);
      setTxError(null);
      return;
    }

    const seq = ++fetchSeqRef.current;
    const caseId = encodeURIComponent(caseData.id);
    const callId = encodeURIComponent(callIdForTx);
    setTxLoading(true);
    setTxError(null);

    fetch(
      `${config.API_BASE_URL}/api/dashboard/cases/${caseId}/transcripts/${callId}`
    )
      .then(async (res) => {
        if (seq !== fetchSeqRef.current) return;
        if (res.status === 404) {
          setTxError(null);
          return;
        }
        if (!res.ok) {
          const text = await res.text();
          setTxError(text || `Transcript fetch failed (${res.status})`);
          return;
        }
        const data = await res.json();
        const entry = data.transcript as TranscriptEntry | undefined;
        if (!entry?.transcript) return;
        const mapped: TranscriptEntry = {
          call_id: String(entry.call_id || callIdForTx),
          timestamp: String(entry.timestamp || ""),
          phone_number: String(entry.phone_number || ""),
          transcript: entry.transcript as TranscriptMessage[],
          success: String(entry.success ?? ""),
        };
        fetchedTxRef.current = {
          ...fetchedTxRef.current,
          [callIdForTx]: mapped,
        };
        setFetchedTxByCallId(fetchedTxRef.current);
      })
      .catch(() => {
        if (seq !== fetchSeqRef.current) return;
        setTxError("Network error while loading transcript.");
      })
      .finally(() => {
        if (seq === fetchSeqRef.current) setTxLoading(false);
      });
  }, [caseData?.id, callIdForTx, embeddedTx]);

  const currentTx = embeddedTx ?? (callIdForTx ? fetchedTxByCallId[callIdForTx] : undefined);
  const outputs = (currentSo?.outputs ?? {}) as Record<string, unknown>;

  const memberName = formatPersonName(callRow?.memberFirstName, callRow?.memberLastName);

  const scheduledForDetail = (() => {
    const at = caseData?.outbound_scheduled_for;
    if (!at || !String(at).trim()) return undefined;
    const s = String(at).trim();
    const last = (caseData.last_vapi_call_id && String(caseData.last_vapi_call_id).trim()) || "";
    const cid = (currentSo?.call_id && String(currentSo.call_id).trim()) || "";
    if (!soHistory.length) return s;
    if (last && cid === last) return s;
    return undefined;
  })();

  return (
    <Sheet open={!!callRow} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex h-full max-h-[100dvh] flex-col overflow-hidden sm:max-w-[min(50vw,720px)]">
        <SheetHeader>
          <SheetTitle>Call Details</SheetTitle>
        </SheetHeader>

        {callRow && caseData && (
          <div className="mt-4 flex min-h-0 flex-1 flex-col space-y-5 overflow-y-auto pr-1">
            {/* Case & Call IDs */}
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Case ID</div>
              <div className="font-mono text-sm break-all">{caseData.id}</div>
              {currentSo?.call_id && (
                <>
                  <div className="text-xs text-muted-foreground mt-2">Call ID</div>
                  <div className="font-mono text-sm break-all">
                    {currentSo.call_id}
                  </div>
                </>
              )}
            </div>

            <PrecallStatusNotice
              status={caseData.precall_structured_output_status}
              error={caseData.precall_structured_output_error}
              updatedAt={caseData.precall_structured_output_status_updated_at}
            />

            <AttemptSelector
              attempts={soHistory}
              value={attemptIdx}
              onChange={setAttemptIdx}
            />

            {/* Status Badges */}
            <div className="flex flex-wrap gap-2">
              <ResultBadge success={currentSo?.success ?? callRow.success} />
              {(outputs.pa_status as string) && (
                <EnumBadge value={`PA: ${String(outputs.pa_status)}`} />
              )}
              {(outputs.fail_reason as string) && (
                <EnumBadge
                  value={String(outputs.fail_reason)}
                  variant="destructive"
                />
              )}
            </div>

            {/* Detail text */}
            {((outputs.pa_details as string) || (outputs.fail_details as string)) && (
              <p className="text-sm">
                {(outputs.pa_details as string) || (outputs.fail_details as string)}
              </p>
            )}

            {/* Reference number */}
            {(outputs.reference_number as string) && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Reference #:</span>
                <span className="font-mono font-medium">
                  {outputs.reference_number as string}
                </span>
              </div>
            )}

            <Separator />

            <CallInfoSection
              phoneNumber={
                currentTx?.phone_number ?? currentSo?.phone_number ?? callRow.phoneNumber
              }
              duration={outputs.call_duration as number | undefined}
              timestamp={currentTx?.timestamp ?? currentSo?.timestamp}
              scheduledFor={scheduledForDetail}
              voiceProvider={
                // Prefer the per-attempt value (written into structured_outputs_history
                // going forward). Fall back to case-level voice_provider only for the
                // most-recent attempt, so older attempts read "—" rather than the
                // misleading latest provider.
                currentSo?.voice_provider ??
                (attemptIdx === soHistory.length - 1 ? caseData.voice_provider : undefined)
              }
              nextRetryProvider={
                attemptIdx === soHistory.length - 1
                  ? caseData.voice_provider_override
                  : undefined
              }
            />

            <Separator />

            <PatientInfoSection
              name={memberName}
              dob={callRow.memberDob ?? "-"}
              memberId={callRow.memberId ?? "-"}
              payer={callRow.payerName ?? "-"}
              medication={callRow.productName ?? "-"}
            />

            <Separator />

            {/* Structured Output */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Structured Output</h4>
              <StructuredOutputView outputs={outputs} />
            </div>

            <Separator />

            {/* Transcript (embedded when present; otherwise lazy-loaded by call_id) */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Transcript</h4>
              {currentTx?.transcript ? (
                <TranscriptView messages={currentTx.transcript} />
              ) : txLoading ? (
                <p className="text-sm text-muted-foreground italic">
                  Loading transcript…
                </p>
              ) : txError ? (
                <p className="text-sm text-destructive italic">{txError}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No transcript for this attempt
                </p>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
